import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  supportTicketService,
  SupportTicket,
} from "@core/services/SupportTicketService";
import { supportFeedbackService } from "@core/services/SupportFeedbackService";
import { FeedbackCollector } from "../components/support/FeedbackCollector";
import { AlertCircle, Loader2 } from "lucide-react";

export const SupportFeedbackPage: React.FC = () => {
  const { t } = useTranslation("forms");
  const { ticketId } = useParams<{ ticketId: string }>();
  const [ticket, setTicket] = useState<SupportTicket | null>(null);
  const [alreadySubmitted, setAlreadySubmitted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!ticketId) {
      setError(t("support.feedback.error_invalid_link"));
      setLoading(false);
      return;
    }

    const load = async () => {
      try {
        const [details, existingFeedback] = await Promise.all([
          supportTicketService.getTicketDetails(ticketId),
          supportFeedbackService.getFeedbackByTicket(ticketId),
        ]);
        setTicket(details.ticket);
        setAlreadySubmitted(!!existingFeedback);
      } catch {
        setError(t("support.feedback.error_expired"));
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [ticketId]);

  return (
    <div className="min-h-screen-safe bg-[#030712] text-white flex flex-col">
      {/* Header */}
      <header className="border-b border-white/5 px-6 py-4">
        <div className="max-w-lg mx-auto flex items-center justify-between">
          <span className="text-white font-semibold text-lg">DooQuest</span>
          <span className="text-gray-500 text-sm">
            {t("support.feedback.header_support")}
          </span>
        </div>
      </header>

      {/* Main content */}
      <main className="flex-1 flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-lg">
          {loading && (
            <div className="flex flex-col items-center justify-center py-16 gap-4">
              <Loader2 className="w-8 h-8 text-purple-400 animate-spin" />
              <p className="text-gray-400 text-sm">
                {t("support.feedback.loading")}
              </p>
            </div>
          )}

          {!loading && error && (
            <div className="flex flex-col items-center justify-center py-16 gap-4 text-center">
              <div className="w-14 h-14 rounded-full bg-red-500/10 flex items-center justify-center">
                <AlertCircle className="w-7 h-7 text-red-400" />
              </div>
              <h1 className="text-xl font-semibold text-white">
                {t("support.feedback.invalid_link_title")}
              </h1>
              <p className="text-gray-400 text-sm max-w-xs">{error}</p>
            </div>
          )}

          {!loading && !error && ticket && (
            <FeedbackCollector
              ticketId={ticketId!}
              ticketSubject={ticket.subject}
              alreadySubmitted={alreadySubmitted}
            />
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-white/5 px-6 py-4 text-center">
        <p className="text-gray-600 text-xs">
          {t("support.feedback.footer_tagline")}
        </p>
      </footer>
    </div>
  );
};
